import React from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './Ads.styles';
import {
  getMyAdCardActionsConfig,
  MyAdCardActionHandlers,
} from './Ads.helpers';
import { AdsEmpty } from '@/features/Profile/sections/MyAds/components/AdsEmpty/AdsEmpty';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { AdStatus } from '@/types';
import { MyAdCardActions } from '@/features/Profile/sections/MyAds/components/MyAdCardActions';
import { AdCardNew } from '@/components/AdCardNew';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { useMyAdsList } from '@/features/Profile/sections/MyAds/hooks/useMyAdsList';
import type { Ad } from '@/api/types/common.types';
import { ArchiveBoxIcon } from '@/components/UI/Icons/Icons';
import { useResponsive } from '@/hooks/useResponsive';
import { useFreezeProfileSidebarWhile } from '@/features/Profile/core/ProfileLayout/ProfileLayout.hooks';

const InfiniteLoader = dynamic(
  () =>
    import('@/components/InfiniteLoader/InfiniteLoader').then(
      mod => mod.InfiniteLoader
    ),
  { ssr: false }
);

type Props = {
  selectedAdStatus: AdStatus;
  actionHandlers: MyAdCardActionHandlers;
  onArchiveClick: (ad: Ad) => void;
};

export const Ads = ({
  selectedAdStatus,
  actionHandlers,
  onArchiveClick,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { ads, canLoadMore, handleLoadMore, isLoadingMore, loading } =
    useMyAdsList(selectedAdStatus);
  const { isDesktop } = useResponsive();
  useFreezeProfileSidebarWhile(isDesktop && loading);

  const actionConfig = getMyAdCardActionsConfig(
    selectedAdStatus,
    t,
    theme,
    actionHandlers
  );

  function renderAdActions(ad: Ad) {
    if (!actionConfig) return null;

    return <MyAdCardActions ad={ad} actions={actionConfig} />;
  }

  function renderAdCard(ad: Ad) {
    const isArchiveButtonVisible = selectedAdStatus === AdStatus.Expired;

    return (
      <S.CardWrapper key={ad.id}>
        <AdCardNew ad={ad} hasBookmarkButton={false} hasStatusOverlay={false} />
        {isArchiveButtonVisible && (
          <S.ArchiveButtonBox>
            <S.ArchiveTooltip
              content={t('pages.cabinet.move_to_archive')}
              offset={7}
              placement="bottom"
            >
              <S.ArchiveButton
                data-testid={CommonTestIds.adCards.archiveButton}
                onClick={() => onArchiveClick(ad)}
              >
                <ArchiveBoxIcon />
              </S.ArchiveButton>
            </S.ArchiveTooltip>
          </S.ArchiveButtonBox>
        )}
        {renderAdActions(ad)}
      </S.CardWrapper>
    );
  }

  function renderActionsSkeleton() {
    if (!actionConfig) return null;

    const hasMenuActions = Boolean(actionConfig.menuActions?.length);

    if (!actionConfig.primaryAction && !hasMenuActions) return null;

    return (
      <S.ActionsSkeleton>
        {actionConfig.primaryAction && <S.PrimaryActionSkeleton />}
        {hasMenuActions && <S.MenuActionSkeleton />}
      </S.ActionsSkeleton>
    );
  }

  function renderLoadingAdCard(index: number) {
    return (
      <S.CardWrapper key={`loading-${index}`}>
        <AdCardNew />
        {renderActionsSkeleton()}
      </S.CardWrapper>
    );
  }

  return (
    <>
      {loading && (
        <S.Grid data-testid={CommonTestIds.adCards.cardsBox}>
          {Array.from({ length: AdsChunkSize.Middle }, (_, index) =>
            renderLoadingAdCard(index)
          )}
        </S.Grid>
      )}
      {!loading && ads.length > 0 && (
        <S.Grid data-testid={CommonTestIds.adCards.cardsBox}>
          {ads.map(renderAdCard)}
          {isLoadingMore &&
            Array.from({ length: AdsChunkSize.Small }, (_, index) =>
              renderLoadingAdCard(index)
            )}
        </S.Grid>
      )}
      {!loading && ads.length === 0 && <AdsEmpty />}

      {!loading && ads.length > 0 && canLoadMore && (
        <InfiniteLoader onVisit={handleLoadMore} rootMargin={200} />
      )}
    </>
  );
};
